import statusDisplayCheckIcon from "assets/images/status-display-check-icon.svg";
import statusDisplayChevronIcon from "assets/images/status-display-chevron-icon.svg";
import statusDisplayEmptyIcon from "assets/images/status-display-empty-icon.svg";
import statusDisplayErrorIcon from "assets/images/status-display-error-icon.svg";
import classNames from "classnames";

import styles from "./StatusDisplay.module.css";

type Status = "finished" | "unfinished" | "error";

type Placement = "left" | "middle" | "right";

const statusIconMap = {
  finished: statusDisplayCheckIcon,
  unfinished: statusDisplayEmptyIcon,
  error: statusDisplayErrorIcon,
};

type StatusStageProps = {
  label: string;
  status: Status;
  placement: Placement;
  hideOnDesktop?: boolean;
};

function StatusStage({
  label,
  status,
  placement,
  hideOnDesktop = false,
}: StatusStageProps) {
  const leftStyle = classNames({
    [styles.bar]: true,
    [styles.barEmpty]: placement === "left",
    ...(placement !== "left" && {
      [styles.barFinished]: status === "finished" || status === "error",
      [styles.barUnfinished]: status === "unfinished",
    }),
  });

  const rightStyle = classNames({
    [styles.bar]: true,
    [styles.barEmpty]: placement === "right",
    ...(placement !== "right" && {
      [styles.barFinished]: status === "finished",
      [styles.barUnfinished]: status === "unfinished" || status === "error",
    }),
  });

  const labelStyle = classNames({
    [styles.statusLabel]: true,
    [styles.statusLabelUnfinished]: status === "unfinished",
    [styles.statusLabelError]: status === "error",
  });

  const chevronStyle = classNames({
    [styles.chevronContainer]: true,
    [styles.hideOnDesktop]: hideOnDesktop,
  });

  return (
    <div className={styles.statusStage}>
      <div className={styles.statusLabelContainer}>
        <p className={labelStyle}>{label}</p>
        {placement !== "right" && (
          <div className={chevronStyle}>
            <img
              className={styles.chevron}
              alt=""
              src={statusDisplayChevronIcon}
            />
          </div>
        )}
      </div>

      <div className={styles.iconBarContainer}>
        <div className={styles.iconContainer}>
          <img
            className={styles.icon}
            alt={status}
            src={statusIconMap[status]}
          />
        </div>
        <div className={leftStyle} />
        <div className={rightStyle} />
      </div>
    </div>
  );
}

type StatusDisplayProps = {
  labels: string[];
  current?: number;
  error?: boolean;
  className?: string;
  hideOnDesktop?: boolean;
};

export default function StatusDisplay({
  labels,
  current = 0,
  error = false,
  className,
  hideOnDesktop = false,
}: StatusDisplayProps) {
  const statusDisplayStyle = classNames(styles.statusDisplay, className);

  return (
    <div className={statusDisplayStyle}>
      {labels.map((label, i) => {
        let status: Status = i <= current ? "finished" : "unfinished";
        if (error && i === current) {
          status = "error";
        }

        let placement: Placement = "middle";
        if (i === 0) {
          placement = "left";
        } else if (i === labels.length - 1) {
          placement = "right";
        }

        return (
          <StatusStage
            key={"StatusStage" + i}
            label={label}
            status={status}
            placement={placement}
            hideOnDesktop={hideOnDesktop}
          />
        );
      })}
    </div>
  );
}
